<script setup lang="ts">
import WorkspaceDetailHero from '@/components/workspace/WorkspaceDetailHero.vue';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ArrowLeft, BellOff, BellRing, BriefcaseBusiness, Building2, Check, ExternalLink, ImagePlus, Link2, Mail, MessageSquare, MoreHorizontal, Pencil, Plus, Rocket, Send, ShieldCheck } from 'lucide-vue-next';
import type { ClientQuickLink } from '~/types/models';

const props = withDefaults(defineProps<{
    presentation?: 'immersive' | 'contained';
    title: string;
    headerImage?: string;
    avatarImage?: string;
    avatarFallback: string;
    entityLabel: string;
    commercialStageLabel: string;
    deliveryStageLabel: string;
    lastContactLabel: string;
    projectCount: number;
    statusLabel: string;
    quickLinks?: ClientQuickLink[];
    inboxHref: string;
    clientsHref: string;
    canMutate: boolean;
    readyToStartOnboarding: boolean;
    startingOnboarding: boolean;
    headerSaving?: boolean;
    avatarSaving?: boolean;
    portalLinkAvailable?: boolean;
    canSendForm?: boolean;
    notificationsMuted?: boolean;
    notificationMuteLoading?: boolean;
}>(), {
    presentation: 'immersive',
    headerImage: '',
    avatarImage: '',
    quickLinks: () => [],
    headerSaving: false,
    avatarSaving: false,
    portalLinkAvailable: false,
    canSendForm: false,
    notificationsMuted: false,
    notificationMuteLoading: false,
});

const emit = defineEmits<{
    (e: 'edit-image', target: 'avatar' | 'header'): void;
    (e: 'manage-quick-links'): void;
    (e: 'add-project'): void;
    (e: 'start-onboarding'): void;
    (e: 'skip-onboarding'): void;
    (e: 'edit-client'): void;
    (e: 'send-digest'): void;
    (e: 'view-portal'): void;
    (e: 'copy-portal-link'): void;
    (e: 'send-form'): void;
    (e: 'toggle-notifications'): void;
    (e: 'manage-people'): void;
    (e: 'manage-automation'): void;
    (e: 'open-activity'): void;
}>();

function externalHref(value: string): string {
    const href = String(value || '').trim();
    if (!href) return '#';
    if (/^[a-z][a-z\d+.-]*:/i.test(href)) return href;
    return `https://${href}`;
}
</script>

<template>
    <WorkspaceDetailHero
        :presentation="presentation === 'contained' ? 'contained' : 'immersive-extended'"
        :background-mode="presentation === 'contained' ? 'inline' : 'frame'"
        :background-image-url="headerImage"
        :background-alt="`${title} banner`"
        :background-fallback-style="{ backgroundColor: 'color-mix(in srgb, var(--primary) 24%, var(--background))' }"
        :avatar-image-url="avatarImage"
        :avatar-alt="title"
        :avatar-fallback="avatarFallback"
        :title="title"
    >
        <template #background-action>
            <Button v-if="canMutate" variant="secondary" size="sm" class="rounded-full" :disabled="headerSaving" @click="emit('edit-image', 'header')">
                <ImagePlus class="mr-2 size-4" />
                {{ headerImage ? 'Change cover' : 'Add cover' }}
            </Button>
        </template>

        <template #avatar-action>
            <Button v-if="canMutate" size="icon" variant="secondary" class="hero-action-secondary size-8 rounded-full shadow-sm backdrop-blur" :disabled="avatarSaving" @click="emit('edit-image', 'avatar')">
                <Pencil class="size-3.5" />
                <span class="sr-only">{{ avatarImage ? 'Edit client avatar' : 'Add client avatar' }}</span>
            </Button>
        </template>

        <template #badges>
            <span class="hero-badge hero-badge-neutral">{{ entityLabel }}</span>
            <span class="hero-badge hero-badge-neutral">{{ commercialStageLabel }}</span>
            <span class="hero-badge hero-badge-amber">{{ deliveryStageLabel }}</span>
        </template>

        <template #meta>
            <span class="inline-flex items-center gap-2"><MessageSquare class="size-3.5 text-foreground/65" />{{ lastContactLabel }}</span>
            <span class="hidden text-foreground/30 sm:inline">•</span>
            <span class="inline-flex items-center gap-2"><BriefcaseBusiness class="size-3.5 text-foreground/65" />{{ projectCount }} linked project{{ projectCount === 1 ? '' : 's' }}</span>
            <span class="hidden text-foreground/30 sm:inline">•</span>
            <span class="inline-flex items-center gap-2"><span class="size-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(16,185,129,0.18)]" aria-hidden="true" />{{ statusLabel }}</span>
        </template>

        <template #supplemental>
            <a v-for="link in quickLinks.slice(0, 4)" :key="link.id" :href="externalHref(link.url)" target="_blank" rel="noreferrer" class="hero-action-secondary hidden items-center gap-2 rounded-full px-3 py-1.5 text-sm shadow-sm backdrop-blur md:inline-flex">
                <span class="size-2 rounded-full" :style="{ backgroundColor: link.color || 'var(--primary)' }" aria-hidden="true" />
                <span class="truncate">{{ link.label }}</span>
                <ExternalLink class="size-3.5 text-foreground/65" />
            </a>
            <Button v-if="canMutate" variant="ghost" size="sm" class="hidden rounded-full px-3 text-foreground/80 hover:bg-foreground/8 hover:text-foreground md:inline-flex" @click="emit('manage-quick-links')">Manage quick links</Button>
        </template>

        <template #actions>
            <Button class="hero-action-primary rounded-full px-5 shadow-sm backdrop-blur" variant="outline" as-child>
                <NuxtLink :to="inboxHref"><Mail class="mr-2 size-4" />Open inbox</NuxtLink>
            </Button>
            <Button v-if="canMutate" variant="outline" class="hero-action-secondary rounded-full px-5 shadow-sm backdrop-blur" @click="emit('add-project')"><Plus class="mr-2 size-4" />Add project</Button>
            <Button v-if="canMutate && readyToStartOnboarding" variant="outline" class="hero-action-secondary rounded-full px-5 shadow-sm backdrop-blur" @click="emit('start-onboarding')"><Rocket class="mr-2 size-4" />Start onboarding</Button>
            <Button v-if="canMutate && readyToStartOnboarding" variant="outline" class="hero-action-secondary rounded-full px-5 shadow-sm backdrop-blur" :disabled="startingOnboarding" @click="emit('skip-onboarding')"><Check class="mr-2 size-4" />{{ startingOnboarding ? 'Updating…' : 'Skip onboarding' }}</Button>

            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="outline" size="icon" class="hero-action-secondary rounded-full shadow-sm backdrop-blur"><MoreHorizontal class="size-4" /><span class="sr-only">More client actions</span></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-60">
                    <DropdownMenuItem as-child><NuxtLink :to="clientsHref"><ArrowLeft class="mr-2 size-4" />Back to CRM</NuxtLink></DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem v-if="canMutate" @select="emit('edit-client')"><Building2 class="mr-2 size-4" />Client settings</DropdownMenuItem>
                    <DropdownMenuItem @select="emit('manage-people')"><UserRound class="mr-2 size-4" />People and portal access</DropdownMenuItem>
                    <DropdownMenuItem v-if="canMutate" @select="emit('manage-quick-links')"><Link2 class="mr-2 size-4" />Manage quick links</DropdownMenuItem>
                    <DropdownMenuItem v-if="canMutate" @select="emit('manage-automation')"><Rocket class="mr-2 size-4" />Automation settings</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canMutate" />
                    <DropdownMenuItem :disabled="notificationMuteLoading" @select="emit('toggle-notifications')">
                        <BellRing v-if="notificationsMuted" class="mr-2 size-4" />
                        <BellOff v-else class="mr-2 size-4" />
                        {{ notificationsMuted ? 'Unmute client notifications' : 'Mute client notifications' }}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem @select="emit('send-digest')"><Mail class="mr-2 size-4" />Send digest</DropdownMenuItem>
                    <DropdownMenuItem @select="emit('open-activity')"><MessageSquare class="mr-2 size-4" />View activity</DropdownMenuItem>
                    <DropdownMenuItem v-if="canMutate && readyToStartOnboarding" @select="emit('start-onboarding')"><Rocket class="mr-2 size-4" />Start onboarding</DropdownMenuItem>
                    <DropdownMenuItem v-if="canMutate && readyToStartOnboarding" @select="emit('skip-onboarding')"><Check class="mr-2 size-4" />Skip onboarding</DropdownMenuItem>
                    <DropdownMenuItem @select="emit('view-portal')"><ShieldCheck class="mr-2 size-4" />View portal as client</DropdownMenuItem>
                    <DropdownMenuItem v-if="portalLinkAvailable" @select="emit('copy-portal-link')"><ExternalLink class="mr-2 size-4" />Copy portal link</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canMutate && canSendForm" />
                    <DropdownMenuItem v-if="canMutate && canSendForm" @select="emit('send-form')"><Send class="mr-2 size-4" />Send form</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>
    </WorkspaceDetailHero>
</template>
